import { levelFromXp } from './profileDefaults';
import { TIERS } from './rankUtils';

// Every achievement awards exactly one Keystone. Keystones are a
// progression-only currency. Granted nowhere else in the economy.
// and spend as a free draft entry in lieu of the gold cost. The flat
// rate keeps balancing trivial and the reward semantically clean:
// "earn an achievement, earn a draft entry."
export const ACHIEVEMENT_KEYSTONE_REWARD = 1;

// Every achievement gets a unique Viking ornament drawn from the
// PNG library shipped at public/ornaments/achievements/<id>.png. The
// medallion in the panel + the celebration overlay both render this
// ornament as a CSS mask so it tints gold against the heraldic frame
// instead of leaning on raster art for the colour.
export const ACHIEVEMENTS = [
  // First steps
  { id: 'first_match', name: 'Trial by Fire', description: 'Play your first match' },
  { id: 'first_win', name: 'Victorious', description: 'Win your first match' },
  { id: 'first_pack', name: 'Collector', description: 'Open your first booster pack' },
  { id: 'first_deck', name: 'Decksmith', description: 'Build your first custom deck' },

  // Deck library. Detected server-side only, see the note above
  // checkAchievements.
  { id: 'share_deck', name: 'Scribe', description: 'Share a custom deck to the Deck Library' },
  { id: 'deck_likes_10', name: 'Acclaimed', description: 'Earn 10 likes on one of your shared decks' },

  // Win streaks
  { id: 'win_3_streak', name: 'On a Roll', description: 'Win 3 matches in a row' },
  { id: 'win_5_streak', name: 'Unstoppable', description: 'Win 5 matches in a row' },
  { id: 'win_10_streak', name: 'Legendary Streak', description: 'Win 10 matches in a row' },

  // Win milestones
  { id: 'win_10', name: 'Seasoned Fighter', description: 'Win 10 matches' },
  { id: 'win_25', name: 'Veteran', description: 'Win 25 matches' },
  { id: 'win_50', name: 'Champion', description: 'Win 50 matches' },
  { id: 'win_100', name: 'Legend', description: 'Win 100 matches' },

  // Match milestones
  { id: 'play_10', name: 'Regular', description: 'Play 10 matches' },
  { id: 'play_50', name: 'Dedicated', description: 'Play 50 matches' },

  // Level milestones
  { id: 'level_5', name: 'Rising Star', description: 'Reach level 5' },
  { id: 'level_10', name: 'Experienced', description: 'Reach level 10' },
  { id: 'level_20', name: 'Master', description: 'Reach level 20' },
  { id: 'level_30', name: 'Grandmaster', description: 'Reach level 30' },

  // Pack milestones
  { id: 'open_5_packs', name: 'Pack Rat', description: 'Open 5 booster packs' },
  { id: 'open_20_packs', name: 'Hoarder', description: 'Open 20 booster packs' },

  // Set-specific packs
  { id: 'open_gothic', name: 'Gothic Explorer', description: 'Open a Gothic booster pack' },
  { id: 'open_arthurian', name: 'Knight Errant', description: 'Open an Arthurian Legends pack' },
  { id: 'open_beta', name: 'Beta Tester', description: 'Open a Beta booster pack' },

  // Rank achievements
  { id: 'rank_adept', name: 'Adept', description: 'Reach Adept rank' },
  { id: 'rank_expert', name: 'Expert', description: 'Reach Expert rank' },
  { id: 'rank_master', name: 'Master', description: 'Reach Master rank' },
  { id: 'rank_grandmaster', name: 'Grandmaster', description: 'Reach Grandmaster rank' },

  // Collection milestones
  { id: 'collect_50', name: 'Archivist', description: 'Collect 50 unique cards' },
  { id: 'collect_100', name: 'Curator', description: 'Collect 100 unique cards' },
  { id: 'collect_200', name: 'Grand Curator', description: 'Collect 200 unique cards' },
  { id: 'collect_500', name: 'Lorekeeper', description: 'Collect 500 unique cards' },
  { id: 'collect_1000', name: 'Omniscient', description: 'Collect 1000 unique cards' },
  { id: 'collect_all', name: 'The Completionist', description: 'Collect every card in the game' },

  // Complete a set
  { id: 'complete_gothic', name: 'Gothic Complete', description: 'Collect every Gothic card' },
  { id: 'complete_arthurian', name: 'Arthurian Complete', description: 'Collect every Arthurian Legends card' },
  { id: 'complete_beta', name: 'Beta Complete', description: 'Collect every Beta card' },
  { id: 'complete_alpha', name: 'Alpha Complete', description: 'Collect every Alpha card' },
];

// Achievement ornament. A single shared Viking glyph (Figurative
// 029 from the heraldic library) used by the panel medallion and the
// celebration overlay. Kept as a helper that takes an id so callers
// don't have to special-case unlocked-vs-locked rendering; the
// per-achievement-art path was scrapped in favour of one strong
// recognizable silhouette across the board.
const ACHIEVEMENT_ORNAMENT_URL = '/game-assets/ornaments/achievements/default.png';

export function getAchievementOrnamentUrl(id) {
  if (!id) return null;
  return ACHIEVEMENT_ORNAMENT_URL;
}

const ACHIEVEMENT_MAP = new Map(ACHIEVEMENTS.map((a) => [a.id, a]));

export function getAchievement(id) {
  return ACHIEVEMENT_MAP.get(id) || null;
}

// `complete_<set>` achievement key → the card's full `set_id` string.
const SET_COMPLETION_NAMES = {
  gothic: 'Gothic',
  arthurian: 'Arthurian Legends',
  beta: 'Beta',
  alpha: 'Alpha',
};

// A card counts toward "collect every <set> card" only if it can actually
// be pulled from that set's boosters. This mirrors the server's pack-pool
// rule exactly (build-pack-pools.js: Booster product + Standard finish).
// tokens, box-toppers, dust promos and precon-only sites all exist as
// non-Booster printings and would otherwise make these achievements
// impossible to complete (e.g. the Frog / Foot Soldier / Rubble tokens).
//
// Deliberately card-level, not art-group-level: an alt illustration is its
// own card to COLLECT (own tile, own foil, own count) but not its own card
// to complete a set with. Beta re-illustrated four Alpha cards and those
// alt arts are themselves Booster + Standard, so a per-printing check here
// would quietly raise the Beta target by four.
function isBoosterCollectibleInSet(card, fullSetName) {
  return (card.printings || []).some(
    (p) => p.set_id === fullSetName && p.product === 'Booster' && p.foiling === 'S',
  );
}

// Saved decks are stored in app-level state (not on the profile), so
// rules that depend on them take an explicit `customDeckCount` argument.
// "Custom" means a saved deck that is not one of the system-granted
// pre-cons. See `getCustomDeckCount` in app.jsx. Editing a pre-con in
// place doesn't bump the count; the player has to actually build (or
// rename) a deck of their own for `first_deck` to fire.
//
// `maxDeckLikes` is likewise deck-derived: the highest like count across
// the player's shared decks, driving the `deck_likes_10` progress bar. It
// only feeds the bar. That achievement is awarded server-side.
export function getAchievementProgress(id, profile, sorceryCards = null, customDeckCount = 0, maxDeckLikes = 0) {
  // matchHistory is the server's recency list (capped at 50). Fine for
  // computing the current streak (which only needs the last N matches in
  // order), but wrong for lifetime totals. Wins/losses are monotonic
  // counters maintained server-side, so totalMatches / totalWins read
  // from those instead.
  const history = profile.matchHistory || [];
  const totalWins = profile.wins || 0;
  const totalMatches = totalWins + (profile.losses || 0);
  const level = levelFromXp(profile.xp || 0);
  // `collection` rows are keyed by (cardId, artGroup, foiling), so the same
  // card can appear once per illustration per finish. Count distinct cardIds
  // so `Collect 1000 unique cards` means what it says. Collection.length
  // would over-count any card owned in multiple finishes or multiple arts.
  const uniqueCards = new Set((profile.collection || []).map((c) => c.cardId)).size;
  const packsOpened = profile.packsOpened || 0;

  // Current win streak. Order-dependent, must read from history.
  let currentStreak = 0;
  for (const match of history) {
    if (match.won) currentStreak++;
    else break;
  }

  const ownedCardIds = new Set((profile.collection || []).map((c) => c.cardId));

  function setProgress(setName) {
    if (!sorceryCards) return null;
    const fullName = SET_COMPLETION_NAMES[setName];
    if (!fullName) return null;
    const setCards = sorceryCards.filter((c) => isBoosterCollectibleInSet(c, fullName));
    const owned = setCards.filter((c) => ownedCardIds.has(c.unique_id)).length;
    return { current: owned, target: setCards.length };
  }

  const progressMap = {
    first_match: { current: Math.min(totalMatches, 1), target: 1 },
    first_win: { current: Math.min(totalWins, 1), target: 1 },
    first_pack: { current: Math.min(packsOpened, 1), target: 1 },
    first_deck: { current: Math.min(customDeckCount, 1), target: 1 },
    // No `share_deck` entry: it hinges on the server-only is_precon flag,
    // and a target-1 row renders no bar anyway.
    deck_likes_10: { current: Math.min(maxDeckLikes, 10), target: 10 },
    win_3_streak: { current: Math.min(currentStreak, 3), target: 3 },
    win_5_streak: { current: Math.min(currentStreak, 5), target: 5 },
    win_10_streak: { current: Math.min(currentStreak, 10), target: 10 },
    win_10: { current: Math.min(totalWins, 10), target: 10 },
    win_25: { current: Math.min(totalWins, 25), target: 25 },
    win_50: { current: Math.min(totalWins, 50), target: 50 },
    win_100: { current: Math.min(totalWins, 100), target: 100 },
    play_10: { current: Math.min(totalMatches, 10), target: 10 },
    play_50: { current: Math.min(totalMatches, 50), target: 50 },
    level_5: { current: Math.min(level, 5), target: 5 },
    level_10: { current: Math.min(level, 10), target: 10 },
    level_20: { current: Math.min(level, 20), target: 20 },
    level_30: { current: Math.min(level, 30), target: 30 },
    open_5_packs: { current: Math.min(packsOpened, 5), target: 5 },
    open_20_packs: { current: Math.min(packsOpened, 20), target: 20 },
    collect_50: { current: Math.min(uniqueCards, 50), target: 50 },
    collect_100: { current: Math.min(uniqueCards, 100), target: 100 },
    collect_200: { current: Math.min(uniqueCards, 200), target: 200 },
    collect_500: { current: Math.min(uniqueCards, 500), target: 500 },
    collect_1000: { current: Math.min(uniqueCards, 1000), target: 1000 },
    collect_all: { current: uniqueCards, target: sorceryCards?.length || 1104 },
    complete_gothic: setProgress('gothic'),
    complete_arthurian: setProgress('arthurian'),
    complete_beta: setProgress('beta'),
    complete_alpha: setProgress('alpha'),
  };

  return progressMap[id] || null;
}

// Deliberately silent on `share_deck` and `deck_likes_10`. Both are
// detected server-side (valkenhall-server/src/utils/deckAchievements.js)
// and pushed back over the `profile:update` WS channel, because neither is
// knowable here: share_deck turns on the is_precon column, which the
// client never receives, and the tenth like is clicked by someone else
// entirely. Don't add tryUnlock calls for them. The server would just
// re-derive the same answer from better data.
export function checkAchievements(profile, sorceryCards = null, customDeckCount = 0) {
  const unlocked = new Set(profile.achievements || []);
  const newlyUnlocked = [];

  function tryUnlock(id) {
    if (!unlocked.has(id) && ACHIEVEMENT_MAP.has(id)) {
      newlyUnlocked.push(id);
      unlocked.add(id);
    }
  }

  // See note in getAchievementProgress: history is for the streak only;
  // cumulative match/win totals come from the server-authoritative
  // counters so they keep ticking past the recency cap.
  const history = profile.matchHistory || [];
  const totalWins = profile.wins || 0;
  const totalMatches = totalWins + (profile.losses || 0);
  const level = levelFromXp(profile.xp || 0);
  // See note in getAchievementProgress: count distinct cardIds, not
  // collection rows (which double-count cards owned in multiple foilings).
  const uniqueCards = new Set((profile.collection || []).map((c) => c.cardId)).size;
  const packsOpened = profile.packsOpened || 0;

  // First steps
  if (totalMatches >= 1) tryUnlock('first_match');
  if (totalWins >= 1) tryUnlock('first_win');
  if (packsOpened >= 1) tryUnlock('first_pack');
  if (customDeckCount >= 1) tryUnlock('first_deck');

  // Win milestones
  if (totalWins >= 10) tryUnlock('win_10');
  if (totalWins >= 25) tryUnlock('win_25');
  if (totalWins >= 50) tryUnlock('win_50');
  if (totalWins >= 100) tryUnlock('win_100');

  // Match milestones
  if (totalMatches >= 10) tryUnlock('play_10');
  if (totalMatches >= 50) tryUnlock('play_50');

  // Win streaks. Check from most recent
  let currentStreak = 0;
  for (const match of history) {
    if (match.won) {
      currentStreak++;
    } else {
      break;
    }
  }
  if (currentStreak >= 3) tryUnlock('win_3_streak');
  if (currentStreak >= 5) tryUnlock('win_5_streak');
  if (currentStreak >= 10) tryUnlock('win_10_streak');

  // Level milestones
  if (level >= 5) tryUnlock('level_5');
  if (level >= 10) tryUnlock('level_10');
  if (level >= 20) tryUnlock('level_20');
  if (level >= 30) tryUnlock('level_30');

  // Pack milestones
  if (packsOpened >= 5) tryUnlock('open_5_packs');
  if (packsOpened >= 20) tryUnlock('open_20_packs');

  // Set-specific packs
  if (profile.packsOpenedBySet?.gothic >= 1) tryUnlock('open_gothic');
  if (profile.packsOpenedBySet?.arthurian >= 1) tryUnlock('open_arthurian');
  if (profile.packsOpenedBySet?.beta >= 1) tryUnlock('open_beta');

  // Rank achievements. Keyed off the canonical TIERS order from
  // rankUtils so renames there can't silently break unlock checks again.
  const tierIndex = TIERS.indexOf(profile.rank?.tier);
  if (tierIndex >= TIERS.indexOf('adept')) tryUnlock('rank_adept');
  if (tierIndex >= TIERS.indexOf('expert')) tryUnlock('rank_expert');
  if (tierIndex >= TIERS.indexOf('master')) tryUnlock('rank_master');
  if (tierIndex >= TIERS.indexOf('grandmaster')) tryUnlock('rank_grandmaster');

  // Collection milestones
  if (uniqueCards >= 50) tryUnlock('collect_50');
  if (uniqueCards >= 100) tryUnlock('collect_100');
  if (uniqueCards >= 200) tryUnlock('collect_200');
  if (uniqueCards >= 500) tryUnlock('collect_500');
  if (uniqueCards >= 1000) tryUnlock('collect_1000');

  // Set completion. Check if player owns at least one of every card in a set
  if (sorceryCards && sorceryCards.length > 0) {
    const ownedCardIds = new Set((profile.collection || []).map((c) => c.cardId));
    const totalUniqueInGame = sorceryCards.length;

    if (ownedCardIds.size >= totalUniqueInGame) tryUnlock('collect_all');

    const setCardIds = { gothic: [], arthurian: [], beta: [], alpha: [] };

    for (const card of sorceryCards) {
      for (const [setKey, fullName] of Object.entries(SET_COMPLETION_NAMES)) {
        if (isBoosterCollectibleInSet(card, fullName)) setCardIds[setKey].push(card.unique_id);
      }
    }

    for (const [setKey, cardIds] of Object.entries(setCardIds)) {
      if (cardIds.length > 0 && cardIds.every((id) => ownedCardIds.has(id))) {
        tryUnlock(`complete_${setKey}`);
      }
    }
  }

  return newlyUnlocked;
}
